import { Vector3, type Mesh } from 'three';
import { createPlainMaterial } from '../../../builders/geometry-builder/helpers';
import type { MeshStandardMaterial } from 'three';

/**
 * After CSG union/subtraction, the merged surround mesh has no geometry groups.
 * This function classifies each triangle by face-normal direction and creates
 * two geometry groups so a multi-material array can be applied:
 *
 *   Group 0  →  non-front faces (top/bottom edges, inner faces, CSG cuts)  →  plain material
 *   Group 1  →  outward-facing roughly-vertical wall faces                  →  textured material
 *
 * The mesh geometry is mutated in place.
 *
 * @param mesh             The post-CSG merged surround mesh
 * @param texturedMat      The textured material for front faces
 * @returns true if classification succeeded and materials were applied
 */
export const classifySurroundFaces = (
	mesh: Mesh,
	texturedMat: MeshStandardMaterial
): boolean => {
	const geometry = mesh.geometry;
	const positions = geometry.attributes.position;
	if (!positions || positions.count < 3) return false;

	// Compute bounding box center as reference point for outward direction
	geometry.computeBoundingBox();
	const bbox = geometry.boundingBox!;
	const centroid = new Vector3();
	bbox.getCenter(centroid);

	const isIndexed = geometry.index !== null;
	const triCount = isIndexed
		? geometry.index!.count / 3
		: positions.count / 3;

	// Temporary vectors
	const vA = new Vector3();
	const vB = new Vector3();
	const vC = new Vector3();
	const edge1 = new Vector3();
	const edge2 = new Vector3();
	const faceNormal = new Vector3();
	const faceCenter = new Vector3();
	const outwardDir = new Vector3();

	// Classify each triangle: true = front (textured), false = other (plain)
	const isFront: boolean[] = new Array(triCount);

	for (let t = 0; t < triCount; t++) {
		let iA: number, iB: number, iC: number;

		if (isIndexed) {
			const idx = geometry.index!;
			iA = idx.getX(t * 3);
			iB = idx.getX(t * 3 + 1);
			iC = idx.getX(t * 3 + 2);
		} else {
			iA = t * 3;
			iB = t * 3 + 1;
			iC = t * 3 + 2;
		}

		vA.fromBufferAttribute(positions, iA);
		vB.fromBufferAttribute(positions, iB);
		vC.fromBufferAttribute(positions, iC);

		// Compute face normal via cross product
		edge1.subVectors(vB, vA);
		edge2.subVectors(vC, vA);
		faceNormal.crossVectors(edge1, edge2).normalize();

		// Face center
		faceCenter.set(
			(vA.x + vB.x + vC.x) / 3,
			(vA.y + vB.y + vC.y) / 3,
			(vA.z + vB.z + vC.z) / 3
		);

		// Direction from mesh centroid to face center (in XZ plane)
		outwardDir.subVectors(faceCenter, centroid);
		outwardDir.y = 0; // project onto horizontal plane

		// A face is "front" if:
		// 1. Its normal is roughly horizontal (|normal.y| < 0.5)
		// 2. Its normal points outward (positive dot product with centroid→face direction in XZ)
		const isHorizontalNormal = Math.abs(faceNormal.y) < 0.5;
		const normalXZ = new Vector3(faceNormal.x, 0, faceNormal.z);
		const dotOutward = normalXZ.dot(outwardDir);

		isFront[t] = isHorizontalNormal && dotOutward > 0;
	}

	// Count triangles in each group
	let plainCount = 0;
	let frontCount = 0;
	for (let t = 0; t < triCount; t++) {
		if (isFront[t]) frontCount++;
		else plainCount++;
	}

	if (frontCount === 0 || plainCount === 0) {
		// All faces are the same type — no point in splitting
		return false;
	}

	// Rearrange triangles: plain first, then front
	if (isIndexed) {
		// === Indexed geometry ===
		const indexArray = geometry.index!.array;
		const newIndex = new (indexArray.constructor as { new(length: number): typeof indexArray })(indexArray.length);
		let writePos = 0;

		// Write plain triangles first
		for (let t = 0; t < triCount; t++) {
			if (!isFront[t]) {
				newIndex[writePos++] = indexArray[t * 3];
				newIndex[writePos++] = indexArray[t * 3 + 1];
				newIndex[writePos++] = indexArray[t * 3 + 2];
			}
		}
		// Then front triangles
		for (let t = 0; t < triCount; t++) {
			if (isFront[t]) {
				newIndex[writePos++] = indexArray[t * 3];
				newIndex[writePos++] = indexArray[t * 3 + 1];
				newIndex[writePos++] = indexArray[t * 3 + 2];
			}
		}

		// Copy back
		for (let i = 0; i < newIndex.length; i++) {
			indexArray[i] = newIndex[i];
		}
		geometry.index!.needsUpdate = true;
	} else {
		// === Non-indexed geometry ===
		// Need to rearrange all vertex attributes
		const plainTriIndices: Array<number> = [];
		const frontTriIndices: Array<number> = [];

		for (let t = 0; t < triCount; t++) {
			if (isFront[t]) frontTriIndices.push(t);
			else plainTriIndices.push(t);
		}

		const newTriOrder = [...plainTriIndices, ...frontTriIndices];

		for (const attrName of Object.keys(geometry.attributes)) {
			const attr = geometry.attributes[attrName];
			const itemSize = attr.itemSize;
			const srcArray = attr.array;
			const tempArray = srcArray.slice(); // copy all data

			let destVertex = 0;
			for (const triIdx of newTriOrder) {
				for (let v = 0; v < 3; v++) {
					const srcVertex = triIdx * 3 + v;
					const srcOffset = srcVertex * itemSize;
					const destOffset = destVertex * itemSize;
					for (let c = 0; c < itemSize; c++) {
						srcArray[destOffset + c] = tempArray[srcOffset + c];
					}
					destVertex++;
				}
			}

			attr.needsUpdate = true;
		}
	}

	// Set up geometry groups
	geometry.clearGroups();
	geometry.addGroup(0, plainCount * 3, 0);            // group 0 → plain material
	geometry.addGroup(plainCount * 3, frontCount * 3, 1); // group 1 → textured material

	// Create and apply materials
	const plainMat = createPlainMaterial(texturedMat);
	mesh.material = [plainMat, texturedMat];

	return true;
};
